import {
  AppChannel,
  PaymentInitiation,
  PaymentMethod,
  PaymentProvider,
  PaymentProviderId,
  PaymentRequest,
  PaymentReturn,
  PaymentVerification,
  ProviderCapabilities,
} from '../types';
import { paymentApi } from '../services/paymentApi';

/**
 * ─── FlexPayProvider (mobile money + carte bancaire branchés) ───────────────
 *
 * Mobile money RDC (doc FlexPay v1.5) — flux PUSH :
 *   1. `initiate`  → backend `POST /payments/flexpay/initiate` qui relaie vers
 *                    FlexPay `POST /paymentService` (merchant/type=1/reference/
 *                    phone/amount/currency/callbackUrl) → `{ orderNumber }`.
 *                    L'utilisateur reçoit un push à valider par PIN → on
 *                    retourne `{ kind: 'push_sent' }` et le front poll `verify`.
 *   2. `verify`    → backend `GET /payments/flexpay/status/{orderNumber}` qui
 *                    relaie vers FlexPay `GET /check/{orderNumber}` ; status
 *                    "0" → 'success', "1" → 'failed', sinon 'pending'.
 *
 * Carte bancaire (doc Card v1.2) — flux REDIRECTION :
 *   1. `initiate`  → backend `POST /payments/flexpay/card/initiate` qui relaie
 *                    vers l'URL carte FlexPay → `{ url, orderNumber }` ; on
 *                    redirige le navigateur vers `url` (page hébergée).
 *   2. retour      → approve/cancel/decline_url ramènent sur /payment/return,
 *                    puis `verify` (même endpoint `/check`) tranche le statut.
 *
 * Le token FlexPay, le code marchand et les URLs (API, carte, callback) vivent
 * côté SERVEUR (jamais exposés au navigateur).
 */
export class FlexPayProvider implements PaymentProvider {
  readonly id = PaymentProviderId.FLEXPAY;
  readonly displayName = 'FlexPay (carte & mobile money)';
  readonly capabilities: ProviderCapabilities = {
    // Carte bancaire (monde) + mobile money RDC (toutes intégrations).
    methods: [PaymentMethod.BANK_CARD, PaymentMethod.MOBILE_MONEY],
    currencies: ['USD', 'CDF'],
    usesRedirect: true,
  };

  supportsChannel(channel: AppChannel): boolean {
    // FlexPay est l'agrégateur du canal web (monde + RDC).
    return channel === AppChannel.WEB;
  }

  async initiate(request: PaymentRequest): Promise<PaymentInitiation> {
    // Mobile money : flux push (le seul branché aujourd'hui).
    if (request.method === PaymentMethod.MOBILE_MONEY) {
      if (!request.phone) {
        return {
          kind: 'error',
          message: 'Numéro de téléphone requis pour le paiement mobile money.',
          retriable: false,
        };
      }
      const { data } = await paymentApi.initiateFlexPay({
        reference: request.reference,
        planId: request.plan.id,
        phone: request.phone,
        method: request.method,
      });
      return {
        kind: 'push_sent',
        orderNumber: data.orderNumber,
        instructions: data.message,
      };
    }

    // Carte bancaire (doc Card v1.2) : le backend génère l'URL de la page de
    // paiement hébergée ; on redirige le navigateur dessus. Au retour,
    // approve/cancel/decline_url ramènent sur /payment/return (parseReturn).
    const { data } = await paymentApi.initiateFlexPayCard({
      reference: request.reference,
      planId: request.plan.id,
      method: request.method,
      returnUrls: request.returnUrls,
    });
    return {
      kind: 'redirect',
      url: data.url,
      orderNumber: data.orderNumber,
    };
  }

  async verify(orderNumber: string): Promise<PaymentVerification> {
    const { data } = await paymentApi.flexPayStatus(orderNumber);
    return {
      status: data.status,
      transaction: data.transaction
        ? {
            reference: data.transaction.reference,
            orderNumber: data.transaction.orderNumber ?? orderNumber,
            amount: data.transaction.amount,
            currency: data.transaction.currency as 'USD' | 'CDF',
          }
        : null,
      message: data.message,
    };
  }

  parseReturn(params: URLSearchParams): PaymentReturn | null {
    // Nos routes internes de retour portent ?provider=flexpay&outcome=…
    // (le backend passera ces URLs à FlexPay en approve/cancel/decline_url).
    if (params.get('provider') !== this.id) return null;
    const outcome = params.get('outcome');
    if (outcome !== 'approved' && outcome !== 'cancelled' && outcome !== 'declined') {
      return null;
    }
    return {
      outcome,
      reference: params.get('reference'),
      orderNumber: params.get('orderNumber'),
    };
  }
}
